import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  UPDATE_REQUEST_MESSAGES,
  describeUpdateRequestFailure,
  isUpdateRequestAccepted,
  lookupManageRsvp,
  submitUpdateRequest,
} from "./ticketOpsApi";
import TicketStatusBadge from "./TicketStatusBadge";
import { getHostedTicketEventDetails } from "./hostedTicketEvents";
import "./rockin-road-home.css";

// SEC-HARD-005 Phase 2C: the manage page never renders a transport, Supabase,
// or RPC message. A lookup that could not complete says so in plain language
// and points at the one action that helps.
const LOOKUP_UNAVAILABLE_MESSAGE =
  "We’re having trouble loading your RSVP right now. Please refresh the page, or try again in a moment.";

function personName(value) {
  return [value?.first_name, value?.last_name].filter(Boolean).join(" ");
}

function attendeeName(ticket) {
  return [ticket?.attendee_first_name, ticket?.attendee_last_name].filter(Boolean).join(" ");
}

function formatEventDate(value) {
  if (!value?.event_date_utc) return "";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: value.event_timezone || "America/Chicago",
  }).format(new Date(value.event_date_utc));
}

export default function RsvpManagePage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [formError, setFormError] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;

    async function loadRsvp() {
      setStatus("loading");
      setMessage("");
      const response = await lookupManageRsvp(token);
      if (cancelled) return;

      setData(response);
      setStatus("loaded");
      if (response?.ok === false) {
        setMessage(LOOKUP_UNAVAILABLE_MESSAGE);
      }
    }

    loadRsvp();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event) {
    event.preventDefault();

    // SEC-HARD-005 Phase 2C: the disabled button is the visible guard; this is
    // the one that holds when a second submit event arrives anyway (a double
    // tap, or Enter pressed while the first request is still in flight).
    if (submitStatus === "submitting") return;

    const trimmedMessage = requestMessage.trim();
    if (!trimmedMessage) {
      setFormError(UPDATE_REQUEST_MESSAGES.MESSAGE_REQUIRED);
      return;
    }

    setFormError("");
    setSubmitStatus("submitting");
    const response = await submitUpdateRequest(token, trimmedMessage, requesterEmail);
    setSubmitStatus("idle");

    // A new request, a retry the server recognized as one it already accepted,
    // and a correction coalesced into the pending request are all confirmed
    // acceptance, so all three clear the form. Nothing else does: an
    // unconfirmed submission keeps the attendee's message exactly where it is.
    if (isUpdateRequestAccepted(response)) {
      setMessage("Your update request has been submitted.");
      setRequestMessage("");
      setRequesterEmail("");
      return;
    }

    // The expired-link notice is rendered once, from `data`, so setting a
    // message here as well would say the same thing to the attendee twice.
    if (response?.found === false) {
      setData(response);
      setMessage("");
      return;
    }

    // describeUpdateRequestFailure, never response.error: the attendee is shown
    // plain language, and no backend, Supabase, or RPC text can reach the page.
    setFormError(describeUpdateRequestFailure(response));
  }

  const rsvp = data?.rsvp;
  const eventDetails = getHostedTicketEventDetails(rsvp?.event_id);
  const tickets = Array.isArray(rsvp?.tickets) ? rsvp.tickets : [];

  return (
    <main className="rrh-page rrh-ops-page">
      <section className="rrh-ops-panel rrh-ops-panel--wide" aria-labelledby="rrh-rsvp-manage-heading">
        <div>
          <p className="rrh-section__eyebrow">RSVP management</p>
          <h1 id="rrh-rsvp-manage-heading">Manage RSVP</h1>
        </div>

        {status === "loading" ? (
          <p className="rrh-state-message">Loading RSVP...</p>
        ) : (
          <>
            {message && (
              <p className={`rrh-state-message ${data?.ok === false || data?.found === false ? "rrh-state-message--error" : "rrh-state-message--success"}`}>
                {message}
              </p>
            )}
            {data?.found === false && (
              <p className="rrh-state-message rrh-state-message--error">{UPDATE_REQUEST_MESSAGES.LINK_INVALID}</p>
            )}

            {rsvp && (
              <>
                <div className="rrh-ticket-summary">
                  <dl>
                    <div>
                      <dt>Event</dt>
                      <dd>{rsvp.event_name || "Road Home LIVE"}</dd>
                    </div>
                    {formatEventDate(rsvp) && (
                      <div>
                        <dt>Date</dt>
                        <dd>{formatEventDate(rsvp)}</dd>
                      </div>
                    )}
                    {eventDetails?.venue && (
                      <div>
                        <dt>Venue</dt>
                        <dd>{eventDetails.venue}<br />{eventDetails.address}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Name</dt>
                      <dd>{personName(rsvp) || "Guest"}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{rsvp.email || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Party size</dt>
                      <dd>{rsvp.family_size || tickets.length || 1}</dd>
                    </div>
                    <div>
                      <dt>RSVP state</dt>
                      <dd><TicketStatusBadge status={rsvp.rsvp_state || rsvp.status} /></dd>
                    </div>
                  </dl>
                </div>

                <div className="rrh-manage-ticket-list">
                  <h2>Tickets</h2>
                  <ul>
                    {tickets.map((ticket) => (
                      <li key={ticket.ticket_id || ticket.ticket_code}>
                        <span>
                          <strong>{ticket.ticket_code || "Ticket"}</strong>
                          {attendeeName(ticket) && <em>{attendeeName(ticket)}</em>}
                        </span>
                        <TicketStatusBadge status={ticket.ticket_status} />
                      </li>
                    ))}
                  </ul>
                </div>

                {eventDetails ? (
                  <div className="rrh-ops-form">
                    <h2>Need to make a change?</h2>
                    <p>
                      Use the ticket cancellation link in your confirmation email, or email{" "}
                      <a href="mailto:events@roadhomeprogram.org">
                        events@roadhomeprogram.org
                      </a>{" "}
                      for other RSVP updates.
                    </p>
                  </div>
                ) : (
                  <form className="rrh-ops-form" onSubmit={handleSubmit}>
                    <h2>Request an update</h2>
                    <label className="rrh-field">
                      <span>What needs to change?</span>
                      <textarea
                        aria-invalid={Boolean(formError)}
                        maxLength={2000}
                        onChange={(event) => setRequestMessage(event.target.value)}
                        required
                        value={requestMessage}
                      />
                      {formError && <span className="rrh-field__error">{formError}</span>}
                    </label>

                    <label className="rrh-field">
                      <span>Requester email <em>optional</em></span>
                      <input
                        onChange={(event) => setRequesterEmail(event.target.value)}
                        type="email"
                        value={requesterEmail}
                      />
                    </label>

                    <button
                      className="rrh-button rrh-button--primary"
                      disabled={submitStatus === "submitting"}
                      type="submit"
                    >
                      {submitStatus === "submitting" ? "Submitting..." : "Submit Update Request"}
                    </button>
                  </form>
                )}
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}
